import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import type { Location } from 'react-router-dom'
import type { OtpChannel, SendOtp, VerifyOtp } from '@/modules/auth/use-cases'
import {
  EmailLinkedToDifferentAccountError,
  InvalidOtpError,
  NetworkError,
  OtpExpiredError,
  OtpSendFailedError,
  PhoneLinkedToDifferentAccountError,
  RateLimitedError,
  isOtpSendRefusal,
  sendRefusalMessage,
} from '@/shared/domain'
import { formatResendLabel, useOtpSendCooldown, useSubmitGuard } from '@/shared/ui/hooks'
import { isValidEmail } from '@/modules/auth/ui/validation'

export type UseOtpVerificationStepProps = {
  identifier: string
  channel: OtpChannel
  onSendOtp: SendOtp
  onVerifyOtp: VerifyOtp
  onVerifySuccess?: (accessToken: string) => Promise<void> | void
  onBack: () => void
}

export type UseOtpVerificationStepResult = {
  code: string
  isSubmitting: boolean
  hasError: boolean
  errorMessage: string
  resendSeconds: number
  resendLabel: string
  resendDisabled: boolean
  activeIdentifier: string
  isEditingIdentifier: boolean
  draftIdentifier: string
  isResendingToNew: boolean
  canSubmitNewIdentifier: boolean
  newIdentifierCooldownSeconds: number
  setCode: (v: string) => void
  handleVerify: (code: string) => void
  handleResend: () => void
  startEditIdentifier: () => void
  cancelEditIdentifier: () => void
  setDraftIdentifier: (v: string) => void
  submitNewIdentifier: () => Promise<void>
}

export function useOtpVerificationStep(props: UseOtpVerificationStepProps): UseOtpVerificationStepResult {
  const { identifier, channel, onSendOtp, onVerifyOtp, onVerifySuccess } = props
  const navigate = useNavigate()
  const location = useLocation()

  const [code, setCode] = useState<string>('')
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [activeIdentifier, setActiveIdentifier] = useState<string>(identifier)
  const [isEditingIdentifier, setIsEditingIdentifier] = useState<boolean>(false)
  const [draftIdentifier, setDraftIdentifier] = useState<string>('')
  const { isSubmitting, runGuarded: runVerifyGuarded } = useSubmitGuard()
  const { isSubmitting: isResendingToNew, runGuarded: runResendGuarded } = useSubmitGuard()
  const {
    secondsLeft: effectiveResendSeconds,
    applyRefusal,
    recordSend,
    clearCooldown,
  } = useOtpSendCooldown({
    channel,
    identifier: activeIdentifier,
  })
  const trimmedDraft = draftIdentifier.trim()
  const {
    secondsLeft: draftCooldownSecondsLeft,
    applyRefusal: applyDraftRefusal,
    recordSend: recordDraftSend,
  } = useOtpSendCooldown({ channel, identifier: trimmedDraft })

  useEffect(() => {
    setActiveIdentifier(identifier)
  }, [identifier])

  const handleVerify = useCallback(
    async (nextCode: string) => {
      if (nextCode.length !== 6) return
      setErrorMessage('')
      await runVerifyGuarded(async () => {
        try {
          const { accessToken } = await onVerifyOtp({ identifier: activeIdentifier, channel, token: nextCode })
          if (onVerifySuccess) {
            await onVerifySuccess(accessToken)
          } else {
            const state = location.state as { from?: Location } | null
            const target = state?.from?.pathname ?? '/'
            navigate(target, { replace: true })
          }
        } catch (err) {
          if (err instanceof InvalidOtpError) {
            setErrorMessage('Invalid code. Please try again.')
            setCode('')
          } else if (err instanceof OtpExpiredError) {
            setErrorMessage('Code expired. Request a new one.')
            clearCooldown()
          } else if (err instanceof PhoneLinkedToDifferentAccountError) {
            setErrorMessage(err.message)
          } else if (err instanceof RateLimitedError) {
            setErrorMessage('Too many attempts. Try again in a few minutes.')
          } else if (err instanceof NetworkError) {
            setErrorMessage('Network error. Please try again.')
          } else {
            setErrorMessage('Something went wrong. Please try again.')
          }
        }
      })
    },
    [
      activeIdentifier,
      channel,
      clearCooldown,
      location.state,
      navigate,
      onVerifyOtp,
      onVerifySuccess,
      runVerifyGuarded,
    ],
  )

  const handleResend = useCallback(async () => {
    if (effectiveResendSeconds > 0) return
    setErrorMessage('')
    await runResendGuarded(async () => {
      try {
        await onSendOtp({ identifier: activeIdentifier, channel })
        recordSend()
      } catch (err) {
        if (
          err instanceof EmailLinkedToDifferentAccountError ||
          err instanceof PhoneLinkedToDifferentAccountError
        ) {
          setErrorMessage(err.message)
        } else if (isOtpSendRefusal(err)) {
          setErrorMessage(sendRefusalMessage(err, false))
          applyRefusal(err)
        } else if (err instanceof OtpSendFailedError) {
          setErrorMessage(err.message)
        } else if (err instanceof NetworkError) {
          setErrorMessage('Network error. Please try again.')
        } else {
          setErrorMessage('Something went wrong. Please try again.')
        }
      }
    })
  }, [activeIdentifier, applyRefusal, channel, effectiveResendSeconds, onSendOtp, recordSend, runResendGuarded])

  const startEditIdentifier = useCallback(() => {
    setDraftIdentifier(activeIdentifier)
    setIsEditingIdentifier(true)
    setErrorMessage('')
  }, [activeIdentifier])

  const cancelEditIdentifier = useCallback(() => {
    setIsEditingIdentifier(false)
    setDraftIdentifier('')
  }, [])

  const canSubmitNewIdentifier =
    draftCooldownSecondsLeft === 0 &&
    (channel === 'email'
      ? isValidEmail(trimmedDraft) && trimmedDraft !== activeIdentifier
      : trimmedDraft !== '' && trimmedDraft !== activeIdentifier)

  const submitNewIdentifier = useCallback(async () => {
    if (!canSubmitNewIdentifier) return
    setErrorMessage('')
    await runResendGuarded(async () => {
      try {
        await onSendOtp({ identifier: trimmedDraft, channel })
        recordDraftSend()
        setActiveIdentifier(trimmedDraft)
        setCode('')
        setIsEditingIdentifier(false)
        setDraftIdentifier('')
      } catch (err) {
        if (
          err instanceof EmailLinkedToDifferentAccountError ||
          err instanceof PhoneLinkedToDifferentAccountError
        ) {
          setErrorMessage(err.message)
        } else if (isOtpSendRefusal(err)) {
          setErrorMessage(sendRefusalMessage(err, false))
          applyDraftRefusal(err)
        } else if (err instanceof OtpSendFailedError) {
          setErrorMessage(err.message)
        } else if (err instanceof NetworkError) {
          setErrorMessage('Network error. Please try again.')
        } else {
          setErrorMessage('Something went wrong. Please try again.')
        }
      }
    })
  }, [
    applyDraftRefusal,
    canSubmitNewIdentifier,
    channel,
    onSendOtp,
    recordDraftSend,
    runResendGuarded,
    trimmedDraft,
  ])

  return {
    code,
    isSubmitting,
    hasError: errorMessage !== '',
    errorMessage,
    resendSeconds: effectiveResendSeconds,
    resendLabel: formatResendLabel(effectiveResendSeconds),
    resendDisabled: effectiveResendSeconds > 0 || isResendingToNew,
    activeIdentifier,
    isEditingIdentifier,
    draftIdentifier,
    isResendingToNew,
    canSubmitNewIdentifier,
    newIdentifierCooldownSeconds: draftCooldownSecondsLeft,
    setCode,
    handleVerify,
    handleResend,
    startEditIdentifier,
    cancelEditIdentifier,
    setDraftIdentifier,
    submitNewIdentifier,
  }
}
